'use client';

import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { sanitizeDecimalInput } from './decimalInput';
import { useTouchedFields } from './useTouchedFields';
import type { CalculatorField, CalculatorFieldValues } from './types';

export const useCountFields = (initialValues: CalculatorFieldValues) => {
  const [values, setValues] = useState<CalculatorFieldValues>(initialValues);
  const { markTouched, isTouched, resetTouched } = useTouchedFields();

  const updateValue = (label: string, transform: (raw: string) => string) =>
    setValues(previous => ({ ...previous, [label]: transform(previous[label]) }));

  const handleChange = (label: string, value: string) => {
    markTouched(label);
    updateValue(label, () => sanitizeDecimalInput(value));
  };

  const fieldProps = (field: CalculatorField, error?: string) => ({
    label: field.label,
    hint: field.hint,
    placeholder: field.placeholder,
    inputMode: 'decimal' as const,
    value: values[field.label],
    error,
    invalid: Boolean(error),
    onChange: (event: ChangeEvent<HTMLInputElement>) => handleChange(field.label, event.target.value),
  });

  return {
    /** No `normalize`/`commit` pair: a count carries no formatting, so it reads the same mid-edit as settled. */
    values,
    reset: () => {
      resetTouched();
      setValues(initialValues);
    },
    isTouched,
    fieldProps,
  };
};

/** Lets a form component take the factory without restating `CalculatorInput`'s props. */
export type CountFieldProps = ReturnType<typeof useCountFields>['fieldProps'];
